import { useCallback, useContext } from "react";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { IsInDetailSidebarContext } from "@src/lib/contexts/IsInDetailSidebarContext";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";

/**
 * Hook that provides a smart wrapper around the internal _toggleSidebar function.
 * Automatically detects whether the call is coming from inside a non-expanded sidebar
 * and passes the appropriate isExternalCall parameter to preserve navigation context.
 */
export const useToggleDetailSidebar = (): {
  toggleSidebar: (
    e: React.MouseEvent<HTMLElement> | undefined,
    objectId: string,
    sidebarType: DetailSidebarType,
    tableId: string,
  ) => void;
} => {
  const { _toggleSidebar } = useDetailSidebarContext();
  const sidebarLocationContext = useContext(IsInDetailSidebarContext);

  const toggleSidebar = useCallback(
    (
      e: React.MouseEvent<HTMLElement> | undefined,
      objectId: string,
      sidebarType: DetailSidebarType,
      tableId: string,
    ) => {
      // If we're NOT in a non-expanded sidebar, this is an external call
      const isExternalCall = !sidebarLocationContext?.isInDetailSidebar;
      _toggleSidebar(e, objectId, sidebarType, tableId, isExternalCall);
    },
    [_toggleSidebar, sidebarLocationContext?.isInDetailSidebar],
  );

  return { toggleSidebar };
};
